import React, { useState } from 'react';
import Layout from '../components/layout/Layout';
import { Building, AlertTriangle, Award, TrendingUp, ChevronDown, ChevronUp } from 'lucide-react';

// Mock supplier data based on COVID contracts
const SUPPLIERS_DATA = [
    {
        id: 1,
        name: 'COMERCIALIZADORA TECNO-HOSPITALARIA SA DE CV',
        rfc: 'CTE140908DJA',
        contracts: 45,
        totalAmount: 52350000,
        directAwardPct: 100,
        avgContractSize: 1163333,
        categories: ['Gel Antibacterial', 'Material Médico'],
        isFlagged: true,
        flagReasons: ['Alta concentración de adjudicaciones directas']
    },
    {
        id: 2,
        name: 'KING MAR MEXICANA SA DE CV',
        rfc: 'KMM96060518A',
        contracts: 8,
        totalAmount: 15432796,
        directAwardPct: 100,
        avgContractSize: 1929099,
        categories: ['Servicios de Sanitización'],
        isFlagged: true,
        flagReasons: ['Monopolio en categoría de sanitización', '100% adjudicación directa']
    },
    {
        id: 3,
        name: 'CLENPROCESS SA DE CV',
        rfc: 'CLE081013471',
        contracts: 28,
        totalAmount: 8756400,
        directAwardPct: 95,
        avgContractSize: 312728,
        categories: ['Gel Antibacterial', 'Desinfectantes'],
        isFlagged: true,
        flagReasons: ['Alta participación de mercado en categoría']
    },
    {
        id: 4,
        name: 'BICO MEDICAL S A P I DE CV',
        rfc: 'BME170328BT8',
        contracts: 12,
        totalAmount: 8234500,
        directAwardPct: 100,
        avgContractSize: 686208,
        categories: ['Respiradores KN95', 'Material Médico'],
        isFlagged: true,
        flagReasons: ['Contract structuring detected', 'Concentración de mercado']
    },
    {
        id: 5,
        name: 'NUBE MÉDICA, S.A.P.I. DE C.V.',
        rfc: 'NME1703279L2',
        contracts: 35,
        totalAmount: 6890000,
        directAwardPct: 88,
        avgContractSize: 196857,
        categories: ['Cubrebocas'],
        isFlagged: false,
        flagReasons: []
    },
    {
        id: 6,
        name: 'VDS COMERCIALIZADORA, S.A. DE C.V.',
        rfc: 'VCO120615LL3',
        contracts: 3,
        totalAmount: 9508371,
        directAwardPct: 100,
        avgContractSize: 3169457,
        categories: ['Caretas de Protección'],
        isFlagged: true,
        flagReasons: ['Monto promedio muy alto', 'Solo 3 contratos con alto valor']
    },
    {
        id: 7,
        name: 'LAVADORAS Y MAQUINAS DE PRESION SA DE CV',
        rfc: 'LMP860507IT2',
        contracts: 5,
        totalAmount: 10502895,
        directAwardPct: 100,
        avgContractSize: 2100579,
        categories: ['Servicios de Limpieza'],
        isFlagged: false,
        flagReasons: []
    },
    {
        id: 8,
        name: 'SYTBA, S.A. DE C.V.',
        rfc: 'SYT070920RX7',
        contracts: 18,
        totalAmount: 2856000,
        directAwardPct: 90,
        avgContractSize: 158667,
        categories: ['Termómetros Infrarrojos'],
        isFlagged: false,
        flagReasons: []
    },
];

const Suppliers = () => {
    const [expandedSupplier, setExpandedSupplier] = useState(null);
    const [sortBy, setSortBy] = useState('totalAmount');
    const [showFlaggedOnly, setShowFlaggedOnly] = useState(false);

    const sortedSuppliers = [...SUPPLIERS_DATA]
        .filter(s => !showFlaggedOnly || s.isFlagged)
        .sort((a, b) => b[sortBy] - a[sortBy]);

    const totalContracts = SUPPLIERS_DATA.reduce((sum, s) => sum + s.contracts, 0);
    const totalAmount = SUPPLIERS_DATA.reduce((sum, s) => sum + s.totalAmount, 0);
    const flaggedCount = SUPPLIERS_DATA.filter(s => s.isFlagged).length;

    return (
        <Layout>
            <div className="max-w-7xl mx-auto">
                {/* Header */}
                <div className="mb-8">
                    <h2 className="text-3xl font-display font-bold text-white mb-2 flex items-center gap-3">
                        <Building className="text-purple-400" />
                        Proveedores
                    </h2>
                    <p className="text-zinc-500 font-mono text-sm">
                        Análisis de proveedores de contrataciones COVID-19 CDMX
                    </p>
                </div>

                {/* Summary Stats */}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
                    <div className="bg-surface border border-zinc-800 rounded-lg p-4">
                        <p className="text-zinc-500 text-xs font-mono uppercase mb-1">Proveedores</p>
                        <p className="text-2xl font-display font-bold text-white">{SUPPLIERS_DATA.length}</p>
                    </div>
                    <div className="bg-surface border border-zinc-800 rounded-lg p-4">
                        <p className="text-zinc-500 text-xs font-mono uppercase mb-1">Contratos</p>
                        <p className="text-2xl font-display font-bold text-blue-400">{totalContracts}</p>
                    </div>
                    <div className="bg-surface border border-zinc-800 rounded-lg p-4">
                        <p className="text-zinc-500 text-xs font-mono uppercase mb-1">Monto Total</p>
                        <p className="text-2xl font-display font-bold text-emerald-400">${(totalAmount / 1000000).toFixed(0)}M</p>
                    </div>
                    <div className="bg-surface border border-red-900/30 rounded-lg p-4">
                        <p className="text-zinc-500 text-xs font-mono uppercase mb-1">Con Alertas</p>
                        <p className="text-2xl font-display font-bold text-red-400">{flaggedCount}</p>
                    </div>
                </div>

                {/* Controls */}
                <div className="flex flex-wrap gap-4 mb-6">
                    <button
                        onClick={() => setShowFlaggedOnly(!showFlaggedOnly)}
                        className={`px-4 py-2 rounded-lg text-sm font-mono transition-all ${showFlaggedOnly
                                ? 'bg-red-500/20 text-red-300 border border-red-500/50'
                                : 'bg-zinc-800 text-zinc-400 border border-zinc-700 hover:border-red-500/30'
                            }`}
                    >
                        <AlertTriangle className="inline-block mr-2" size={14} />
                        Solo con Alertas
                    </button>
                    <select
                        value={sortBy}
                        onChange={(e) => setSortBy(e.target.value)}
                        className="bg-zinc-900 border border-zinc-700 rounded-lg px-4 py-2 text-sm text-zinc-300 font-mono focus:border-primary focus:outline-none"
                    >
                        <option value="totalAmount">Ordenar por Monto</option>
                        <option value="contracts">Ordenar por # Contratos</option>
                        <option value="directAwardPct">Ordenar por % Directas</option>
                        <option value="avgContractSize">Ordenar por Tamaño Promedio</option>
                    </select>
                </div>

                {/* Suppliers Table */}
                <div className="bg-surface border border-zinc-800 rounded-lg overflow-hidden">
                    <table className="w-full">
                        <thead>
                            <tr className="border-b border-zinc-800 bg-zinc-900/50">
                                <th className="text-left p-4 text-xs font-mono text-zinc-500 uppercase">Proveedor</th>
                                <th className="text-left p-4 text-xs font-mono text-zinc-500 uppercase">Contratos</th>
                                <th className="text-left p-4 text-xs font-mono text-zinc-500 uppercase">Monto Total</th>
                                <th className="text-left p-4 text-xs font-mono text-zinc-500 uppercase">% Directas</th>
                                <th className="text-left p-4 text-xs font-mono text-zinc-500 uppercase">Estado</th>
                                <th className="text-left p-4 text-xs font-mono text-zinc-500 uppercase"></th>
                            </tr>
                        </thead>
                        <tbody>
                            {sortedSuppliers.map((supplier) => (
                                <React.Fragment key={supplier.id}>
                                    <tr
                                        className={`border-b border-zinc-800/50 hover:bg-zinc-800/30 cursor-pointer transition-colors ${supplier.isFlagged ? 'bg-red-900/5' : ''
                                            }`}
                                        onClick={() => setExpandedSupplier(
                                            expandedSupplier === supplier.id ? null : supplier.id
                                        )}
                                    >
                                        <td className="p-4">
                                            <div className="flex items-start gap-2">
                                                {supplier.isFlagged && (
                                                    <AlertTriangle className="text-red-400 shrink-0 mt-0.5" size={16} />
                                                )}
                                                <div>
                                                    <p className="text-sm font-medium text-white">{supplier.name}</p>
                                                    <p className="text-xs text-zinc-500 font-mono">{supplier.rfc}</p>
                                                </div>
                                            </div>
                                        </td>
                                        <td className="p-4">
                                            <span className="text-sm font-mono text-blue-400">{supplier.contracts}</span>
                                        </td>
                                        <td className="p-4">
                                            <span className="text-sm font-mono font-bold text-emerald-400">
                                                ${(supplier.totalAmount / 1000000).toFixed(1)}M
                                            </span>
                                        </td>
                                        <td className="p-4">
                                            <div className="flex items-center gap-2">
                                                <div className="w-12 h-2 bg-zinc-800 rounded-full overflow-hidden">
                                                    <div
                                                        className={`h-full ${supplier.directAwardPct > 90 ? 'bg-red-500' :
                                                                supplier.directAwardPct > 70 ? 'bg-amber-500' : 'bg-green-500'
                                                            }`}
                                                        style={{ width: `${supplier.directAwardPct}%` }}
                                                    />
                                                </div>
                                                <span className={`text-sm font-mono ${supplier.directAwardPct > 90 ? 'text-red-400' :
                                                        supplier.directAwardPct > 70 ? 'text-amber-400' : 'text-green-400'
                                                    }`}>
                                                    {supplier.directAwardPct}%
                                                </span>
                                            </div>
                                        </td>
                                        <td className="p-4">
                                            {supplier.isFlagged ? (
                                                <span className="px-2 py-1 rounded text-xs font-mono bg-red-900/30 text-red-400">
                                                    ⚠ Alerta
                                                </span>
                                            ) : (
                                                <span className="px-2 py-1 rounded text-xs font-mono bg-green-900/30 text-green-400">
                                                    ✓ Normal
                                                </span>
                                            )}
                                        </td>
                                        <td className="p-4">
                                            {expandedSupplier === supplier.id ? (
                                                <ChevronUp className="text-zinc-400" size={16} />
                                            ) : (
                                                <ChevronDown className="text-zinc-400" size={16} />
                                            )}
                                        </td>
                                    </tr>
                                    {expandedSupplier === supplier.id && (
                                        <tr className="bg-zinc-900/50">
                                            <td colSpan="6" className="p-4">
                                                <div className="grid md:grid-cols-3 gap-4">
                                                    <div>
                                                        <p className="text-xs font-mono text-zinc-500 uppercase mb-2">Categorías</p>
                                                        <div className="flex flex-wrap gap-1">
                                                            {supplier.categories.map((cat, idx) => (
                                                                <span key={idx} className="px-2 py-1 bg-zinc-800 text-zinc-300 text-xs rounded">
                                                                    {cat}
                                                                </span>
                                                            ))}
                                                        </div>
                                                    </div>
                                                    <div>
                                                        <p className="text-xs font-mono text-zinc-500 uppercase mb-2">Contrato Promedio</p>
                                                        <p className="text-lg font-display font-bold text-white">
                                                            ${(supplier.avgContractSize / 1000).toFixed(0)}K MXN
                                                        </p>
                                                    </div>
                                                    <div>
                                                        <p className="text-xs font-mono text-zinc-500 uppercase mb-2">Alertas</p>
                                                        {supplier.flagReasons.length > 0 ? (
                                                            <div className="space-y-1">
                                                                {supplier.flagReasons.map((reason, idx) => (
                                                                    <p key={idx} className="text-xs text-red-400">• {reason}</p>
                                                                ))}
                                                            </div>
                                                        ) : (
                                                            <p className="text-xs text-green-400">Sin alertas detectadas</p>
                                                        )}
                                                    </div>
                                                </div>
                                            </td>
                                        </tr>
                                    )}
                                </React.Fragment>
                            ))}
                        </tbody>
                    </table>
                </div>
            </div>
        </Layout>
    );
};

export default Suppliers;
